import React, { useState, useRef } from 'react';
import '../styles/home.css';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Spinner from '@cloudscape-design/components/spinner';
import { addUsage } from './add-usage.js';

// The AI model + its onnxruntime are self-hosted under public/imgly/ so the
// tool runs fully offline from this app's own origin (no external CDN).
// imgly builds asset URLs with `new URL(file, publicPath)`, which requires an
// ABSOLUTE base URL — so resolve /imgly/ against the page's base href. This
// also stays correct if the app is deployed under a sub-path.
const modelPublicPath = () => new URL('imgly/', document.baseURI).href;

const TransparentBackground = ({ isNew }) => {
  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [resultUrl, setResultUrl] = useState(null);
  const [resultBlob, setResultBlob] = useState(null);
  const [submitValue, setSubmitValue] = useState('Remove Background');
  const [notify, setNotify] = useState('');
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState('');
  const fileInputRef = useRef(null);

  const toggleModal = () => {
    setVisible(!visible);
    resetState();
  };

  const resetState = () => {
    setFile(null);
    setPreview(null);
    setResultUrl(null);
    setResultBlob(null);
    setSubmitValue('Remove Background');
    setNotify('');
    setError(null);
    setProcessing(false);
    setProgress('');
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;
    if (!selectedFile.type.match(/image\/(png|jpeg|jpg)/i)) {
      setNotify('Only PNG, JPG, or JPEG images are allowed');
      setError('error');
      return;
    }

    setFile(selectedFile);
    setNotify('');
    setError(null);
    setResultUrl(null);
    setResultBlob(null);

    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(selectedFile);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const removeBg = async () => {
    if (!file) return;
    setProcessing(true);
    setError(null);
    setNotify('');
    setSubmitValue(<Spinner />);
    setProgress('Loading AI model…');

    try {
      // Loaded on demand — the model + onnxruntime are a large WASM bundle.
      const { removeBackground } = await import('@imgly/background-removal');

      const blob = await removeBackground(file, {
        publicPath: modelPublicPath(),
        model: 'isnet_quint8',
        output: { format: 'image/png' },
        progress: (key, current, total) => {
          // key looks like "fetch:/models/isnet_quint8" or "compute:inference"
          if (key.startsWith('fetch')) {
            const pct = total ? Math.round((current / total) * 100) : 0;
            setProgress(`Downloading AI model… ${pct}% (one-time, then cached)`);
          } else {
            setProgress('Removing background…');
          }
        },
      });

      const url = URL.createObjectURL(blob);
      setResultBlob(blob);
      setResultUrl(url);
      setSubmitValue('Remove Background');
      setProcessing(false);
      setProgress('');
      addUsage('AWESOME PDF TOOLS TransparentBg', '1.0', 1, window.location.href);
    } catch (err) {
      console.error('Background removal failed:', err);
      setNotify('Failed to remove the background. Please try a different image.');
      setError('error');
      setSubmitValue('Remove Background');
      setProcessing(false);
      setProgress('');
    }
  };

  const handleDownload = () => {
    if (!resultUrl) return;
    const base = file ? file.name.replace(/\.[^/.]+$/, '') : 'image';
    const a = document.createElement('a');
    a.href = resultUrl;
    a.download = `${base}_transparent.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div>
      <div className="service-card" onClick={toggleModal}>
        {isNew && <span className="badge badge-new">NEW</span>}
        <div className="service-card-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="#FF9900">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8 0-1.85.63-3.55 1.69-4.9L16.9 18.31A7.902 7.902 0 0112 20zm6.31-3.1L7.1 5.69A7.902 7.902 0 0112 4c4.41 0 8 3.59 8 8 0 1.85-.63 3.55-1.69 4.9z" />
          </svg>
        </div>
        <h3 className="service-card-title">Transparent Background</h3>
        <p className="service-card-desc">Automatically remove the background from any photo using AI</p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={toggleModal}
        visible={visible}
        size="medium"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={toggleModal}>
                Cancel
              </Button>
              {!resultUrl ? (
                <Button variant="primary" disabled={!file || processing || error} onClick={removeBg}>
                  {submitValue}
                </Button>
              ) : (
                <Button variant="primary" onClick={handleDownload}>
                  Download PNG
                </Button>
              )}
            </SpaceBetween>
          </Box>
        }
        header="Remove Background"
      >
        <div>
          <div className="bordered-box">
            <h6 className="container-header">🖼️ Choose Image</h6>
            <FormField label="Upload a PNG, JPG, or JPEG" errorText={notify}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                onChange={handleFileChange}
              />
            </FormField>
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#565959' }}>
              AI automatically detects the subject and removes everything else — works on photos with any background.
              The first run downloads the model (~40&nbsp;MB, then cached); everything stays in your browser.
            </div>
          </div>

          {preview && !resultUrl && (
            <div className="bordered-box">
              <h6 className="container-header">👁️ Preview</h6>
              <div style={{ textAlign: 'center' }}>
                <div style={{ ...checkerStyle, display: 'inline-block', borderRadius: '8px' }}>
                  <img
                    src={preview}
                    alt="Preview"
                    style={{ maxWidth: '240px', maxHeight: '200px', display: 'block', borderRadius: '8px' }}
                  />
                </div>
                <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
                  {file?.name} • {formatFileSize(file?.size || 0)}
                </div>
              </div>
            </div>
          )}

          {processing && (
            <div className="bordered-box">
              <SpaceBetween size="m" direction="vertical" alignItems="center">
                <Spinner size="large" />
                <Box variant="p" color="text-body-secondary">
                  {progress || 'Working…'}
                </Box>
              </SpaceBetween>
            </div>
          )}

          {resultUrl && (
            <div className="bordered-box">
              <h6 className="container-header">✅ Background Removed</h6>
              <div
                style={{
                  backgroundColor: '#e6f7e6',
                  border: '1px solid #52c41a',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                }}
              >
                <span style={{ color: '#135200' }}>🔒 100% Private • AI ran in your browser • exported as PNG</span>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ ...checkerStyle, display: 'inline-block', borderRadius: '8px' }}>
                  <img
                    src={resultUrl}
                    alt="Result"
                    style={{ maxWidth: '280px', maxHeight: '240px', display: 'block', borderRadius: '8px' }}
                  />
                </div>
                {resultBlob && (
                  <div style={{ marginTop: '8px', fontSize: '13px', color: '#999' }}>
                    PNG • {formatFileSize(resultBlob.size)}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'center', marginTop: '12px' }}>
                <Button variant="link" onClick={resetState}>
                  Remove background from another image
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

// Checkerboard so transparency is visible behind the preview.
const checkerStyle = {
  backgroundImage:
    'linear-gradient(45deg, #ddd 25%, transparent 25%), linear-gradient(-45deg, #ddd 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ddd 75%), linear-gradient(-45deg, transparent 75%, #ddd 75%)',
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
  backgroundColor: '#fff',
  padding: '6px',
  border: '1px solid #ddd',
};

export default TransparentBackground;
